import i18n from "../../../i18n";
import {
  IAppointment,
  IAppointmentFilters,
  ICompany,
  IFundingSource,
  ITherapist,
  ViewType,
} from "../types/appointments.types";

/**
 * Builds the payload for the AppointmentsCalendar export (PDF/Excel) from the
 * appointments currently displayed in a calendar view.
 *
 * The backend (`GenericExport` + `AppointmentsCalendarExportTemplate`) groups
 * rows by the "Date" column for PDF, so rows are returned pre-sorted by date+time.
 * Headers are sent as i18n KEYS — the backend resolves them per language.
 */

const tr = (key: string, fallback: string) =>
  i18n.t(key, { defaultValue: fallback }) as string;

const DAY_KEYS = [
  "APPOINTMENTS_DAY_SUNDAY",
  "APPOINTMENTS_DAY_MONDAY",
  "APPOINTMENTS_DAY_TUESDAY",
  "APPOINTMENTS_DAY_WEDNESDAY",
  "APPOINTMENTS_DAY_THURSDAY",
  "APPOINTMENTS_DAY_FRIDAY",
  "APPOINTMENTS_DAY_SATURDAY",
];
const DAY_FALLBACK = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const VIEW_LABEL: Record<ViewType, { key: string; fallback: string }> = {
  daily: { key: "APPOINTMENTS_VIEW_DAILY", fallback: "יומי" },
  weekly: { key: "APPOINTMENTS_VIEW_WEEKLY", fallback: "שבועי" },
  monthly: { key: "APPOINTMENTS_VIEW_MONTHLY", fallback: "חודשי" },
};

const pad = (n: number) => n.toString().padStart(2, "0");

const toDate = (iso: string): Date | null => {
  if (!iso) return null;
  // Handles "2026-06-09" and full ISO strings.
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (iso: string): string => {
  const d = toDate(iso);
  if (!d) return iso || "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const dayName = (iso: string): string => {
  const d = toDate(iso);
  if (!d) return "—";
  const idx = d.getDay();
  return tr(DAY_KEYS[idx], DAY_FALLBACK[idx]);
};

const computeEndTime = (time?: string, duration?: number): string => {
  if (!time) return "—";
  if (!duration) return "—";
  const [h, m] = time.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "—";
  const total = h * 60 + m + duration;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
};

const dash = (v?: string | number | null): string => {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
};

// Patient column: patient name, or group name for group sessions, or free-text comment.
const patientCell = (a: IAppointment): string =>
  dash(a.patientName || a.treatmentGroupName || a.comments);

const treatmentCell = (a: IAppointment): string =>
  dash(a.treatmentTypeName || a.treatmentProgramName || a.treatmentGroupName);

export interface BuildAppointmentsExportOptions {
  viewType: ViewType;
  rangeLabel: string;
  filterSummary?: string[];
}

export interface AppointmentsExportPayload {
  entity: string;
  columns: string[];
  headers: string[];
  dataRows: Record<string, any>[];
  title: string;
  subtitle: string;
  filterSummary?: string[];
}

export function buildAppointmentsExport(
  appointments: IAppointment[],
  { viewType, rangeLabel, filterSummary }: BuildAppointmentsExportOptions
): AppointmentsExportPayload {
  const columns = [
    "Date",
    "Day",
    "Time",
    "EndTime",
    "Duration",
    "Patient",
    "Therapist",
    "Company",
    "Treatment",
    "FundingSource",
    "Room",
  ];
  const headers = [
    "DATE",
    "DAY",
    "APPOINTMENTS_TIME",
    "END_TIME",
    "DURATION",
    "PATIENT_NAME",
    "THERAPIST_NAME",
    "SUB_COMPANY",
    "TREATMENT_TYPE",
    "FUNDING_SOURCE",
    "TREATMENT_ROOM",
  ];

  const minutesLabel = tr("MINUTES_SHORT", "דק׳");

  const sorted = [...appointments].sort((a, b) => {
    const d = (a.date || "").localeCompare(b.date || "");
    if (d !== 0) return d;
    return (a.time || "").localeCompare(b.time || "");
  });

  const dataRows = sorted.map((a) => ({
    Date: formatDate(a.date),
    Day: dayName(a.date),
    Time: dash(a.time),
    EndTime: a.endTime ? dash(a.endTime) : computeEndTime(a.time, a.duration),
    Duration: a.duration ? `${a.duration} ${minutesLabel}` : "—",
    Patient: patientCell(a),
    Therapist: dash(a.therapistName),
    Company: dash(a.patientFakeCompanyName || a.companyName),
    Treatment: treatmentCell(a),
    FundingSource: dash(a.fundingSourceName),
    Room: dash(a.treatmentRoomName),
  }));

  const viewLabelConf = VIEW_LABEL[viewType];
  const viewLabel = tr(viewLabelConf.key, viewLabelConf.fallback);
  const subtitle = `${tr("VIEW", "תצוגה")}: ${viewLabel} · ${rangeLabel}`;

  return {
    entity: "AppointmentsCalendar",
    columns,
    headers,
    dataRows,
    title: tr("APPOINTMENTS_CALENDAR_EXPORT_TITLE", "יומן תורים"),
    subtitle,
    filterSummary,
  };
}

/**
 * Builds the active-filter chips for the admin calendar from the shared filters
 * and the lookup lists available in the appointments context.
 */
export function buildAdminFilterSummary(
  filters: IAppointmentFilters,
  lists: {
    therapistsList: ITherapist[];
    companiesOptions: ICompany[];
    fundingSourcesList: IFundingSource[];
  }
): string[] {
  const chips: string[] = [];

  if (filters.therapistIds && filters.therapistIds.length > 0) {
    const names = filters.therapistIds
      .map((id) => lists.therapistsList.find((t) => t.id === id)?.name)
      .filter(Boolean) as string[];
    if (names.length) chips.push(`${tr("THERAPIST", "מטפל")}: ${names.join(", ")}`);
  }

  if (filters.fakeCompanyIds && filters.fakeCompanyIds.length > 0) {
    const names = filters.fakeCompanyIds
      .map((id) => lists.companiesOptions.find((c) => c.id === id)?.name)
      .filter(Boolean) as string[];
    if (names.length) chips.push(`${tr("SUB_COMPANY", "חברת בת")}: ${names.join(", ")}`);
  }

  if (filters.fundingSourceIds && filters.fundingSourceIds.length > 0) {
    const names = filters.fundingSourceIds
      .map((id) => lists.fundingSourcesList.find((f) => f.id === id)?.name)
      .filter(Boolean) as string[];
    if (names.length)
      chips.push(`${tr("FUNDING_SOURCE", "מקור מימון")}: ${names.join(", ")}`);
  }

  if (filters.patientSearch && filters.patientSearch.trim()) {
    chips.push(`${tr("SEARCH", "חיפוש")}: ${filters.patientSearch.trim()}`);
  }

  return chips;
}
